import {
  ED_ORDER_TYPE_EXPIRATION_PERIOD,
  orderStatusConstants,
  orderTypeConstants,
} from "constants/order";
import { availabilityList } from "constants/paymentPlanConstants";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import utc from "dayjs/plugin/utc";
import { removeClearPatientFee } from "helpers/fee";
import { dollarsToCents, centsToDollars } from "helpers/price";
import { isEmpty } from "lodash";
import { FieldValues } from "react-hook-form";
import { IPaymentPlanTypeEnabled } from "types/Account/Account";
import { IOrder, IOrderCreateRequest } from "types/Order/Order";
import { OrderType } from "types/Order/OrderType";

dayjs.extend(utc);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);

/**
 * Visit date for POST provider_procedures/search (YYYY-MM-DD, UTC calendar day).
 * Accepts Visit form MM/DD/YYYY or an ISO string from the order API.
 */
export function formatDateOfServiceForProviderProcedureSearch(
  raw: string | null | undefined
): string | undefined {
  if (raw == null || String(raw).trim() === "") return undefined;
  const s = String(raw).trim();
  const us = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/(\d{4})$/;
  const um = us.exec(s);
  if (um) {
    return `${um[3]}-${um[1]}-${um[2]}`;
  }
  const iso = dayjs.utc(s, "YYYY-MM-DD", true);
  if (iso.isValid()) {
    return iso.format("YYYY-MM-DD");
  }
  return undefined;
}

export function toCents(aValue: number) {
  return Math.round((Math.abs(aValue) / 100) * 10000);
}

export function removeTimezone(dataString: string): Date {
  return dayjs.utc(dataString).toDate();
}

export function convertToUTC(dateString: string) {
  const [month, day, year] = dateString.split("/").map(Number);

  // Create a Date object in UTC timezone
  const utcDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));

  return utcDate.toISOString();
}

export function validateInputTime(time: string) {
  if (!time) return true;

  const regex = /^(1[0-2]|0?[1-9]):([0-5][0-9]) ([APap][Mm])$/;
  return !regex.test(time) ? "Invalid time." : true;
}

export function validateInputDatePicker(
  input: string,
  blockDays: "past" | "future" | "any",
  orderType?: string,
  notRequired?: boolean
) {
  const pattern = /^\d{2}\/\d{2}\/\d{4}$/;

  if (notRequired && !input) {
    return true;
  }

  if (!pattern.test(input)) {
    return "Invalid Date";
  }

  const parts = input.split("/");
  const month = parseInt(parts[0], 10);
  const day = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);

  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    year < 1000 ||
    year > 9999
  ) {
    return "Invalid Date";
  }

  const currentDate = dayjs().startOf("day");
  const selectedDate = dayjs(input).startOf("day");

  if (orderType === orderTypeConstants.ED) {
    const tenDaysAgo = currentDate.subtract(
      ED_ORDER_TYPE_EXPIRATION_PERIOD,
      "day"
    );
    if (selectedDate.isBetween(tenDaysAgo, currentDate, "days", "[]")) {
      return true;
    }
    return `Select a date between ${tenDaysAgo.format(
      "MM/DD/YYYY"
    )} and ${currentDate.format("MM/DD/YYYY")}`;
  }

  if (
    blockDays === "future" &&
    selectedDate.isSameOrAfter(currentDate, "date")
  ) {
    return true;
  }

  if (
    blockDays === "past" &&
    selectedDate.isSameOrBefore(currentDate, "date")
  ) {
    return true;
  }

  if (blockDays === "any") {
    return true;
  }

  return "Invalid Date";
}

/** Same rules as Visit "Date of Service" (MM/DD/YYYY + order-type windows, e.g. ED). */
export function isDateOfServiceValidForCptSearch(
  raw: string | null | undefined,
  orderType?: string
): boolean {
  if (raw == null || String(raw).trim() === "") return false;
  return validateInputDatePicker(String(raw).trim(), "any", orderType) === true;
}

export function validateScheduledFirstCommunicationDate(
  input?: Date | string | null,
  dateOfService?: string | null,
  scheduledFirstCommunicationSent?: boolean | null
) {
  if (!input || scheduledFirstCommunicationSent) {
    return true;
  }
  const selectedDate = dayjs(input);
  if (!selectedDate.isValid()) return "Invalid Date";
  const minAllowed = dayjs().add(2, "minute").startOf("minute");
  if (selectedDate.isBefore(minAllowed)) {
    return "Pick a time at least 2 min from now.";
  }
  const dos = dayjs(dateOfService);
  if (!dos.isValid()) return "Service date is invalid.";
  const lastAllowed = dos.endOf("day");
  if (selectedDate.isAfter(lastAllowed)) {
    return "Pick a date no later than 1 day before service date.";
  }
  return true;
}

const getServiceDetailsObjectByOrderType = ({
  orderType,
  serviceDetails,
  amount,
  outOfPocketReason,
  estimatedVisitCostForPatient,
  payerName,
  payerName2,
  placeOfService,
  locality,
  totalInsuranceAllowable,
  secondaryInsuranceActivated,
  clearPatientFee,
  practiceType,
  practiceType2,
}: FieldValues) => {
  const patientResponsibilityObj = {
    patientResponsibilityEstimateAmountInCents: toCents(amount) || 0,
    procedures: serviceDetails || [],
    totalInsuranceAllowableInCents:
      totalInsuranceAllowable !== undefined && totalInsuranceAllowable !== null
        ? toCents(totalInsuranceAllowable)
        : null,
  };

  // For GFE orders, we need to calculate total including Clear Patient Fee, if it exists
  const totalWithClearPatientFee = (amount || 0) + (clearPatientFee || 0);

  const GFEObj = {
    procedures: serviceDetails || [],
    patientResponsibilityEstimateAmountInCents:
      toCents(totalWithClearPatientFee) || 0,
    payerName,
    secondaryPayerName: secondaryInsuranceActivated ? payerName2 : null,
    placeOfService,
    locality,
    practiceTypeCode: practiceType,
    secondaryPracticeTypeCode: practiceType2,
  };

  const defaultObj = {
    procedures: serviceDetails || [],
    estimatedVisitCost: toCents(estimatedVisitCostForPatient) || 0,
    outOfPocketReason: outOfPocketReason?.name,
  };

  switch (orderType) {
    case orderTypeConstants.PATIENT_RESPONSIBILITY:
      return patientResponsibilityObj;
    case orderTypeConstants.GFE:
      return GFEObj;
    default:
      return defaultObj;
  }
};

const getBenefitDetailsObjectByOrderType = ({
  orderType,
  benefitDetails,
}: FieldValues) => {
  if (orderType !== orderTypeConstants.GFE) {
    return null;
  }
  return benefitDetails;
};

const getCostApplicationRulesByOrderType = ({
  orderType,
  facilityCopay,
  facilityCoinsurance,
  facilityDeductible,
  professionalCopay,
  professionalCoinsurance,
  professionalDeductible,
}: FieldValues) => {
  if (orderType !== orderTypeConstants.GFE) {
    return null;
  }
  return {
    facilityRules: {
      useCopay: facilityCopay,
      useCoinsurance: facilityCoinsurance,
      useDeductible: facilityDeductible,
    },
    nonFacilityRules: {
      useCopay: professionalCopay,
      useCoinsurance: professionalCoinsurance,
      useDeductible: professionalDeductible,
    },
  };
};

const getPatientBenefitsByOrderType = ({
  orderType,
  deductible,
  outOfPocketMax,
  flatCopay,
  professionalFlatCopay,
  coinsurancePercent,
  facilityCopay,
  facilityCoinsurance,
  facilityDeductible,
  professionalCopay,
  professionalCoinsurance,
  professionalDeductible,
  benefitsUpdatedAt,
}: FieldValues) => {
  if (orderType !== orderTypeConstants.GFE) {
    return null;
  }
  const deductibleInCents = dollarsToCents(deductible || 0);
  const outOfPocketMaxInCents = dollarsToCents(outOfPocketMax || 0);
  const flatCopayInCents = dollarsToCents(flatCopay || 0);
  const professionalFlatCopayInCents = dollarsToCents(
    professionalFlatCopay || 0
  );
  return {
    deductible: deductibleInCents,
    outOfPocketMax: outOfPocketMaxInCents,
    flatCopay: flatCopayInCents,
    professionalFlatCopay: professionalFlatCopayInCents,
    coinsurancePercent,
    costApplicationRules: getCostApplicationRulesByOrderType({
      orderType,
      facilityCopay,
      facilityCoinsurance,
      facilityDeductible,
      professionalCopay,
      professionalCoinsurance,
      professionalDeductible,
    }),
    benefitsUpdatedAt,
  };
};

const getSecondaryPatientBenefitsByOrderType = ({
  orderType,
  deductible2,
  outOfPocketMax2,
  flatCopay2,
  professionalFlatCopay2,
  coinsurancePercent2,
  facilityCopay2,
  facilityCoinsurance2,
  facilityDeductible2,
  professionalCopay2,
  professionalCoinsurance2,
  professionalDeductible2,
  secondaryInsuranceActivated,
  secondaryBenefitsUpdatedAt,
}: FieldValues) => {
  if (orderType !== orderTypeConstants.GFE || !secondaryInsuranceActivated) {
    return null;
  }
  const deductibleInCents = dollarsToCents(deductible2 || 0);
  const outOfPocketMaxInCents = dollarsToCents(outOfPocketMax2 || 0);
  const flatCopayInCents = dollarsToCents(flatCopay2 || 0);
  const professionalFlatCopayInCents = dollarsToCents(
    professionalFlatCopay2 || 0
  );
  return {
    deductible: deductibleInCents,
    outOfPocketMax: outOfPocketMaxInCents,
    flatCopay: flatCopayInCents,
    professionalFlatCopay: professionalFlatCopayInCents,
    coinsurancePercent: coinsurancePercent2,
    costApplicationRules: getCostApplicationRulesByOrderType({
      orderType,
      facilityCopay: facilityCopay2,
      facilityCoinsurance: facilityCoinsurance2,
      facilityDeductible: facilityDeductible2,
      professionalCopay: professionalCopay2,
      professionalCoinsurance: professionalCoinsurance2,
      professionalDeductible: professionalDeductible2,
    }),
    benefitsUpdatedAt: secondaryBenefitsUpdatedAt,
  };
};

export const parseDateOfService = ({
  dateOfService,
  timeOfService,
}: FieldValues) => {
  const timeOfServiceParsed = timeOfService?.toLowerCase();

  const date = dayjs.utc(dateOfService).toDate();

  if (timeOfService && timeOfService !== "-") {
    const time = dayjs(timeOfServiceParsed, "hh:mm a").toDate();

    date.setHours(time.getHours());
    date.setMinutes(time.getMinutes());
    date.setSeconds(time.getSeconds());
    date.setMilliseconds(time.getMilliseconds());

    return dayjs(date).utc(true).format();
  }

  return dayjs(date).utc(true).format();
};

export const getOrderStatus = (props: any) => {
  const { orderType } = props;
  if (
    [
      orderTypeConstants.GFE,
      orderTypeConstants.BUNDLED,
      orderTypeConstants.ED,
      orderTypeConstants.BARIATRICS,
    ].includes(orderType) &&
    props.totalAmount === 0
  ) {
    return orderStatusConstants.NO_BALANCE_DUE;
  }
  if (
    orderType === orderTypeConstants.PATIENT_RESPONSIBILITY &&
    props.amount === 0
  ) {
    return orderStatusConstants.NO_BALANCE_DUE;
  }
  return props.status || orderStatusConstants.SENT_TO_PATIENT;
};

export const getPayerIdByOrderType = (props: any) => {
  if (props.orderType === orderTypeConstants.GFE) {
    return props.payer.payerId;
  }
  return null;
};

export const getSecondaryPayerIdByOrderType = (props: any) => {
  if (
    props.orderType === orderTypeConstants.GFE &&
    props.secondaryInsuranceActivated
  ) {
    return props?.payer2?.payerId || null;
  }
  return null;
};

export const getScheduledFirstCommunicationDate = (props: FieldValues) => {
  const { scheduledFirstCommunicationDate } = props;
  if (!scheduledFirstCommunicationDate) {
    return null;
  }
  return dayjs(scheduledFirstCommunicationDate)
    .utc()
    .format("YYYY-MM-DD HH:mm");
};

export const getScheduledCommunicationCustomText = (props: FieldValues) => {
  const customText = props?.customText;
  const scheduledFirstCommunicationDate =
    props?.scheduledFirstCommunicationDate;
  const scheduledFirstCommunicationSent =
    props?.scheduledFirstCommunicationSent;
  if (
    scheduledFirstCommunicationDate &&
    !scheduledFirstCommunicationSent &&
    !props.sendDefaultText
  ) {
    return customText;
  }
  return null;
};

export const mapFormToRequestBody = (
  props: FieldValues,
  saveAsDraft?: boolean
) => {
  return {
    dateOfService: parseDateOfService(props),
    accountNumber: props.accountNumber,
    serviceDetails: getServiceDetailsObjectByOrderType(props),
    amountInCents: toCents(props.totalAmount) || 0,
    status: saveAsDraft ? orderStatusConstants.DRAFT : getOrderStatus(props),
    orderType: props.orderType,
    orderAttemptNumber: props.orderAttemptNumber || 0,
    patient: {
      lastName: props.lastName,
      firstName: props.firstName,
      dateOfBirth: convertToUTC(props.dateOfBirth),
      email: props.email || undefined,
      phoneNumber: props.phoneNumber || undefined,
      preferredContactMethod: props.preferredContactMethod,
      patientBenefits: {
        isSubscriberPatient: props.isSubscriberPatient,
        memberID: props.memberId,
      },
      secondaryPatientBenefits: props.secondaryInsuranceActivated
        ? {
            isSubscriberPatient: props.isSubscriberPatient,
            memberID: props.memberId2,
          }
        : null,
    },
    accountId: props.accountId,
    gfeQuoteId: props.gfeQuoteId,
    orderId: props.orderId || undefined,
    payerId: getPayerIdByOrderType(props),
    secondaryPayerId: getSecondaryPayerIdByOrderType(props),
    paymentPlanDuration: props.isDownPaymentOnly
      ? 1
      : props.paymentPlanDuration,
    paymentPlanMinDownPaymentPercent: props.isDownPaymentOnly
      ? props.downpaymentOnlyPercent
      : props.paymentPlanMinDownPaymentPercent,
    paymentPlanEnabled: props.isDownPaymentOnly
      ? true
      : props.paymentPlanEnabled,
    paymentPlanMinDownPaymentFlat: props.isDownPaymentOnly
      ? dollarsToCents(
          props.downpaymentOnlyFlatAmountWithFees ||
            props.downpaymentOnlyFlatAmount
        ) || null
      : dollarsToCents(
          props?.flatAmountWithFees || props?.paymentPlanMinDownPaymentFlat
        ) || null,
    providerDistributionLogic:
      props.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY
        ? null
        : props.providerDistributionLogic,
    isDownPaymentOnly: props.isDownPaymentOnly,
    paymentPlanCalculationBasis: props.isSetAmount
      ? "InstallmentAmount"
      : "Duration",
    installmentFrequency: props.isSetAmount
      ? props.installmentFrequency
      : "Monthly",
    installmentAmountInCents: props.isSetAmount
      ? dollarsToCents(props.installmentAmount || 0)
      : null,
    customText: props.sendDefaultText ? null : props.customText,
    benefitDetails: getBenefitDetailsObjectByOrderType({
      orderType: props.orderType,
      benefitDetails: props.benefitDetails,
    }),
    secondaryBenefitDetails: props.secondaryInsuranceActivated
      ? getBenefitDetailsObjectByOrderType({
          orderType: props.orderType,
          benefitDetails: props.benefitDetails2,
        })
      : null,
    outreachStopped: !props.outreachEnabled,
    patientBenefits: getPatientBenefitsByOrderType(props),
    secondaryPatientBenefits: getSecondaryPatientBenefitsByOrderType(props),
    pverifyResponseId: props.pverifyResponseId || undefined,
    secondaryPverifyResponseId: props.secondaryPverifyResponseId || undefined,
    scheduledFirstCommunicationDate: getScheduledFirstCommunicationDate(props),
    scheduledCommunicationCustomText:
      getScheduledCommunicationCustomText(props),
    accountLocationId: props.accountLocationId || null,
    excludedNcciRulesetIds: props.excludedNcciRulesetIds || [],
    excludedNcciInfo: props.excludedNcciInfo || [],
  } as IOrderCreateRequest;
};

export const buildURLforProviderProcedure = ({
  baseUrl,
  payerNameField,
  accountId,
  placeOfServiceField,
  localityField,
  cptCodeField,
  cptDescription,
  providerName,
  specialty,
  connectedHospital,
  page,
  pageSize,
  onlyLowerThanMedicare,
  volume,
  volumeMatchMode,
  feeEffectiveDate,
  contractFeeScheduleIds,
  defaultPagination = true,
}: {
  baseUrl: string;
  payerNameField?: string;
  accountId?: string;
  placeOfServiceField?: string;
  localityField?: string;
  cptCodeField?: string;
  cptDescription?: string;
  providerName?: string;
  specialty?: string;
  connectedHospital?: string;
  page?: number;
  pageSize?: number;
  onlyLowerThanMedicare?: boolean;
  volume?: number;
  volumeMatchMode?: string;
  /** ISO calendar date (YYYY-MM-DD) — resolve contract fee amounts as of this term. */
  feeEffectiveDate?: string;
  contractFeeScheduleIds?: string[];
  defaultPagination?: boolean;
}): string => {
  const params = [
    !isEmpty(payerNameField) ? `payerName=${payerNameField}` : null,
    !isEmpty(placeOfServiceField)
      ? `placeOfService=${placeOfServiceField}`
      : null,
    !isEmpty(localityField) ? `locality=${localityField}` : null,
    !isEmpty(cptCodeField) ? `cptCode=${cptCodeField}` : null,
    !isEmpty(cptDescription) ? `cptDescription=${cptDescription}` : null,
    !isEmpty(providerName) ? `providerName=${providerName}` : null,
    !isEmpty(specialty) ? `specialty=${specialty}` : null,
    !isEmpty(connectedHospital)
      ? `connectedHospital=${connectedHospital}`
      : null,
    onlyLowerThanMedicare ? `onlyLowerThanMedicare=true` : null,
    volume !== undefined ? `volume=${volume}` : undefined,
    !isEmpty(volumeMatchMode) ? `volumeMatchMode=${volumeMatchMode}` : null,
    feeEffectiveDate != null && !isEmpty(feeEffectiveDate)
      ? `feeEffectiveDate=${encodeURIComponent(feeEffectiveDate)}`
      : null,
    ...(contractFeeScheduleIds ?? []).map(
      (id) => `contractFeeScheduleIds=${encodeURIComponent(id)}`
    ),
  ].filter(Boolean);

  if (accountId === undefined) {
    if ((page === undefined || pageSize === undefined) && defaultPagination) {
      return `${baseUrl}?page=0&pageSize=25${
        !isEmpty(params) ? "&" : ""
      }${params.join("&")}`;
    }
    const queryParams = [
      page !== undefined ? `page=${page}` : null,
      pageSize !== undefined ? `pageSize=${pageSize}` : null,
      ...params,
    ].filter(Boolean);
    return `${baseUrl}${queryParams.length ? `?${queryParams.join("&")}` : ""}`;
  }
  return `${baseUrl}?accountId=${accountId}${
    !isEmpty(params) ? "&" : ""
  }${params.join("&")}`;
};

const orderTypeMap: Record<OrderType, string> = {
  GFE: "gfe",
  Bundled: "bundled",
  ED: "ed",
  PatientResponsibility: "patientResponsibility",
  Bariatrics: "bariatrics",
};

export function getPaymentPlanEnabled(
  orderType: OrderType,
  paymentPlanTypeEnabled: IPaymentPlanTypeEnabled,
  order?: IOrder
): boolean {
  const key = orderTypeMap[orderType] as keyof IPaymentPlanTypeEnabled;
  const isEnabled = paymentPlanTypeEnabled[key];

  if (!isEmpty(order)) {
    switch (order.account.paymentPlanAvailability) {
      case availabilityList.SAME_DAY_DATE_CREATION: {
        const dateOfCreationIsSameOfToday = dayjs
          .utc()
          .isSameOrAfter(order.createdAt, "day");
        return isEnabled && dateOfCreationIsSameOfToday;
      }
      case availabilityList.THREE_DAY_BEFORE_DATE_OF_SERVICE: {
        const today = dayjs().utc().format("YYYY-MM-DD");
        const threeDayBeforeDateOfService = dayjs
          .utc(order.dateOfService)
          .subtract(3, "days")
          .isSameOrBefore(today, "day");
        return isEnabled && threeDayBeforeDateOfService;
      }
      case availabilityList.SAME_DAY_DATE_OF_SERVICE: {
        const today = dayjs().utc().format("YYYY-MM-DD");
        const sameDayOfService = dayjs
          .utc(order.dateOfService)
          .isSameOrBefore(today, "day");
        return isEnabled && sameDayOfService;
      }
      default: {
        return isEnabled;
      }
    }
  }
  return isEnabled;
}

export function getAccountLevelPaymentPlanAvailabilityEnabled(
  order: IOrder
): boolean {
  switch (order.account.paymentPlanAvailability) {
    case availabilityList.SAME_DAY_DATE_CREATION: {
      const dateOfCreationIsSameOfToday = dayjs
        .utc()
        .isSameOrAfter(order.createdAt, "day");
      return dateOfCreationIsSameOfToday;
    }
    case availabilityList.THREE_DAY_BEFORE_DATE_OF_SERVICE: {
      const today = dayjs().utc().format("YYYY-MM-DD");
      const threeDayBeforeDateOfService = dayjs
        .utc(order.dateOfService)
        .subtract(3, "days")
        .isSameOrBefore(today, "day");
      return threeDayBeforeDateOfService;
    }
    case availabilityList.SAME_DAY_DATE_OF_SERVICE: {
      const today = dayjs().utc().format("YYYY-MM-DD");
      const sameDayOfService = dayjs
        .utc(order.dateOfService)
        .isSameOrBefore(today, "day");
      return sameDayOfService;
    }
    default: {
      return true;
    }
  }
}

/**
 * Calculate the minimum down payment flat value in dollars,
 * adjusted for clear patient fee if applicable (for GFE orders).
 */
export function getPaymentPlanMinDownPaymentFlatValue({
  paymentPlanMinDownPaymentFlat,
  orderType,
  clearPatientFeeValueInPercentage,
}: {
  paymentPlanMinDownPaymentFlat: number | null | undefined;
  orderType: string;
  clearPatientFeeValueInPercentage?: number;
}): number | null {
  if (
    paymentPlanMinDownPaymentFlat === null ||
    paymentPlanMinDownPaymentFlat === undefined
  ) {
    return null;
  }
  const valueInDollars = centsToDollars(paymentPlanMinDownPaymentFlat);
  // Downgrade value if order has clear patient fee (for GFE orders)
  if (
    orderType === orderTypeConstants.GFE &&
    clearPatientFeeValueInPercentage
  ) {
    return removeClearPatientFee(
      valueInDollars,
      clearPatientFeeValueInPercentage
    );
  }
  return valueInDollars;
}
